// 🔍 MEMORY LEAK TRACKER - Array Buffer & String Accumulation
// Para investigar vazamentos específicos identificados no heap snapshot

// ✅ CONTROLE: Flag para desabilitar em caso de interferências
const TRACKER_ENABLED = process.env.NODE_ENV === 'development' && 
                       !process.env.REACT_APP_DISABLE_MEMORY_TRACKER &&
                       window.location.search.includes('enableMemoryTracker');

interface MemoryLeakTracker {
  arrayBuffers: WeakSet<ArrayBuffer>;
  strings: Set<string>;
  fileReads: Map<string, number>;
  uploads: Map<string, number>;
  largeStrings: Map<string, { size: number, timestamp: number }>;
  blobUrls: Set<string>;
}

class ArrayBufferLeakTracker {
  private static instance: ArrayBufferLeakTracker;
  private tracker: MemoryLeakTracker = {
    arrayBuffers: new WeakSet<ArrayBuffer>(),
    strings: new Set<string>(),
    fileReads: new Map<string, number>(),
    uploads: new Map<string, number>(),
    largeStrings: new Map<string, { size: number, timestamp: number }>(),
    blobUrls: new Set<string>()
  };

  private originalFetch: typeof fetch;
  private originalCreateObjectURL: typeof URL.createObjectURL;

  static getInstance(): ArrayBufferLeakTracker {
    if (!ArrayBufferLeakTracker.instance) {
      ArrayBufferLeakTracker.instance = new ArrayBufferLeakTracker();
    }
    return ArrayBufferLeakTracker.instance;
  }

  init() {
    // ✅ PROTEÇÃO: Só inicializar se explicitamente habilitado
    if (!TRACKER_ENABLED) {
      console.log('🔕 [Memory Tracker] Desabilitado - adicione ?enableMemoryTracker na URL para ativar');
      return;
    }
    
    console.log('🔍 [Memory Tracker] Inicializado - Monitorando vazamentos...');
    this.patchFetch();
    this.patchURLCreateObjectURL();
    this.startPeriodicLogging();
  }

  private patchFetch() {
    this.originalFetch = window.fetch;
    
    // 🔧 DESENVOLVIMENTO: Detectar ambiente local
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1' ||
                         window.location.port !== '';
    
    window.fetch = async (...args) => {
      const url = args[0].toString();
      
      const response = await this.originalFetch.apply(window, args);

      // Interceptar blob() e arrayBuffer()
      const originalBlob = response.blob;
      const originalArrayBuffer = response.arrayBuffer;

      response.blob = async () => {
        const blob = await originalBlob.call(response);
      
        return blob;
      };

      response.arrayBuffer = async () => {
        const buffer = await originalArrayBuffer.call(response);
     
        this.tracker.arrayBuffers.add(buffer);
        
        // Track large buffers
        if (buffer.byteLength > 1024 * 1024) { // > 1MB
          console.warn(`⚠️ [ArrayBuffer Tracker] LARGE BUFFER: ${(buffer.byteLength / 1024 / 1024).toFixed(2)}MB - ${url}`);
        }
        
        return buffer;
      };

      return response;
    };
  }

  private patchURLCreateObjectURL() {
    this.originalCreateObjectURL = URL.createObjectURL;
    
    // 🔧 DESENVOLVIMENTO: Detectar ambiente local
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1' ||
                         window.location.port !== '';
    
    URL.createObjectURL = (object: Blob | MediaSource) => {
      const url = this.originalCreateObjectURL(object);
      
      if (object instanceof Blob) {
        this.tracker.blobUrls.add(url);
      }
      
      return url;
    };

    // Patch revokeObjectURL para tracking
    const originalRevokeObjectURL = URL.revokeObjectURL;
    URL.revokeObjectURL = (url: string) => {
     
      this.tracker.blobUrls.delete(url);
      return originalRevokeObjectURL(url);
    };
  }

  private startPeriodicLogging() {
    setInterval(() => {
      this.logMemoryUsage();
    }, 30000); // A cada 30 segundos
  }

  public logMemoryUsage() {
    const stringCount = this.tracker.strings.size;
    const largeStringCount = this.tracker.largeStrings.size;
    const blobUrlCount = this.tracker.blobUrls.size;
    
    // Calcular tamanho total das strings grandes
    let totalLargeStringSize = 0;
    Array.from(this.tracker.largeStrings.values()).forEach(data => {
      totalLargeStringSize += data.size;
    });

    console.groupEnd();

    // Alert se muita memória
    if (totalLargeStringSize > 50 * 1024 * 1024) { // > 50MB
      console.error(`🚨 [MEMORY ALERT] Large strings using ${(totalLargeStringSize / 1024 / 1024).toFixed(2)}MB!`);
    }

    if (blobUrlCount > 50) {
      console.error(`🚨 [MEMORY ALERT] ${blobUrlCount} Blob URLs not revoked!`);
    }
  }

  getDetailedReport() {
    const stringCount = this.tracker.strings.size;
    const largeStrings = Array.from(this.tracker.largeStrings.entries());
    const blobUrls = Array.from(this.tracker.blobUrls);
    
    let totalLargeStringSize = 0;
    Array.from(this.tracker.largeStrings.values()).forEach(data => {
      totalLargeStringSize += data.size;
    });

    return {
      strings: {
        total: stringCount,
        largeStrings: largeStrings.map(([preview, data]) => ({
          preview,
          sizeKB: Math.round(data.size / 1024),
          ageMinutes: Math.round((Date.now() - data.timestamp) / 60000)
        })),
        totalLargeSizeMB: Math.round(totalLargeStringSize / 1024 / 1024)
      },
      blobs: {
        activeUrls: blobUrls,
        count: blobUrls.length
      }
    };
  }

  // Força limpeza de dados tracked
  forceCleanup() {
    
    // Limpar strings grandes antigas (> 5 minutos)
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    Array.from(this.tracker.largeStrings.entries()).forEach(([key, data]) => {
      if (data.timestamp < fiveMinutesAgo) {
        this.tracker.largeStrings.delete(key);
      }
    });

    // Revogar blob URLs órfãs
    Array.from(this.tracker.blobUrls).forEach(url => {
      try {
        URL.revokeObjectURL(url);
        this.tracker.blobUrls.delete(url);
      } catch (error) {
        // URL já foi revogada
        this.tracker.blobUrls.delete(url);
      }
    });

    // Limpar strings pequenas
    this.tracker.strings.clear();
    
  }
}

// Expor globalmente para debug
const arrayBufferTracker = ArrayBufferLeakTracker.getInstance();

if (typeof window !== 'undefined') {
  (window as any).arrayBufferTracker = arrayBufferTracker;
  (window as any).getMemoryLeakReport = () => arrayBufferTracker.getDetailedReport();
  (window as any).forceMemoryCleanup = () => arrayBufferTracker.forceCleanup();
  (window as any).logMemoryUsage = () => arrayBufferTracker.logMemoryUsage();
}

export { arrayBufferTracker };
export default ArrayBufferLeakTracker;